// lib/modelist/streetDevelop.ts
// "Develop 360": a Street View workspace's panorama, already photographic after Texturise, filled with brand-true shop
// interiors and people in Studio, slice by slice of its horizon, every slice an ordinary generation, all at once and
// nothing attached. The user places the cuts on the canvas; an experiment iterated by editing the recipe below.

import { enforceStudioModelMinimumGenerations } from '#lib/model-registry';
import { MUSE_IMAGE_MAX_RATIO } from '#lib/muse-image';
import { bandCaptured, STREET_BANDS } from '#lib/modelist/streetview';
import type { Rect, StreetCuts } from '#types/types';

/** Degrees on the panorama: latitude +90 at the frame's top, longitude -180 at its left edge (panoDirection's convention). */
type StreetZone = { lat: [number, number]; lon: [number, number] };

type StreetDevelopStep = { zone: StreetZone; prompt: string };

type StreetDevelopRecipe = {
  id: string;
  label: string;
  model: string;
  params: Record<string, unknown>;
  /** Each output harmonized to what lies under it once it lands (a generation's Auto color match). */
  autoColorMatch: boolean;
  /** The model's own check of a generation area: throws why the model refuses it. */
  checkZone: (rect: Rect) => unknown;
  prompt: string;
  /** Introduces a slice's own words at the end of its prompt. */
  noteLead: string;
};

const SLICE_PROMPT = `This image is one slice of a 360° equirectangular panorama of a retail place, seen from eye height. It is already a photograph: keep its projection, viewpoint, light, architecture, materials, every sign and its exact lettering, and add only what follows.

Shops: behind every shopfront glazing, show the real interior of the brand named on that shopfront's sign, as that brand's own stores actually look: the products it actually sells and only those, its store design, fixtures, colours and visual merchandising, lit as a shop is lit. Never show another brand's products, logo or store style there. Where a unit has no readable sign, show a plausible, neutral shop without any brand marking.

People: add a realistic, moderate number of visitors, walking, looking at windows, entering shops, scaled to their distance and standing on the ground, never floating. Their dress and shopping bags fit the brands around them; no logo is invented.

Add no new text, logo or sign. Leave a band of about 5 % along all four edges untouched, with no person or object crossing an edge.`;

const HORIZON = STREET_BANDS.find((band) => band.name === 'horizon')!;

// Muse clamps a shape outside its range instead of refusing it: the slice would come back distorted.
const checkMuseZone = ({ w, h }: Rect) => {
  if (!(w > 0 && h > 0) || Math.max(w / h, h / w) > MUSE_IMAGE_MAX_RATIO) {
    throw new Error(`Muse Image supports generation areas between 1:${MUSE_IMAGE_MAX_RATIO} and ${MUSE_IMAGE_MAX_RATIO}:1.`);
  }
};

export const STREET_DEVELOP_RECIPES: StreetDevelopRecipe[] = [
  { id: 'muse-image-horizon', label: 'Muse Image · shops and visitors', model: 'muse-image', params: {}, autoColorMatch: true, checkZone: checkMuseZone, prompt: SLICE_PROMPT, noteLead: 'In this slice: ' },
];

/** "Texturise", before Develop 360: one generation over the whole panorama that only makes the render more photographic. */
export const STREET_TEXTURE_RECIPES = [{
  id: 'nano-banana-21-texturise',
  label: 'Texturise · Nano Banana 2.1 4K',
  model: 'nano-banana-2.1',
  params: { imageSize: '4K', enable_google_search: false },
  // A whole-frame generation: a colour match or an alignment against the render would undo it.
  autoColorMatch: false,
  autoIntegrate: false,
  // Nano Banana loses the scene under a long prompt.
  prompt: 'Make this render a sharp photograph of the same place, with real materials and textures. Keep the exact geometry and viewpoint, every sign and its lettering, the sunlight and the shadows. Add nothing. Keep the buildings neat and clean.',
}] as const;

/** The cuts before any were placed on a workspace: the whole horizon in one slice. */
export const uncutStreet = (): StreetCuts => ({ at: [], notes: [''] });

export const isStreetCuts = (value: unknown): value is StreetCuts => {
  const cuts = value as StreetCuts;
  return !!cuts && Array.isArray(cuts.at) && cuts.at.every((c, i) => Number.isFinite(c) && c > 0 && c < 1 && (i === 0 || c > cuts.at[i - 1]!))
    && Array.isArray(cuts.notes) && cuts.notes.length === cuts.at.length + 1 && cuts.notes.every((note) => typeof note === 'string');
};

/** The horizon band cut at `cuts`, slices left to right at its captured latitudes, edge to edge, each with its own words. */
export function streetDevelopSteps(recipe: StreetDevelopRecipe, { at, notes }: StreetCuts): StreetDevelopStep[] {
  const edges = [0, ...at, 1];
  const lat = bandCaptured(HORIZON.lat);
  const lon = (fraction: number) => fraction * 360 - 180;
  return edges.slice(1).map((to, i) => {
    const note = notes[i]?.trim();
    return {
      zone: { lat, lon: [lon(edges[i]!), lon(to)] },
      prompt: note ? `${recipe.prompt}\n\n${recipe.noteLead}${note}` : recipe.prompt,
    };
  });
}

const EPSILON = 1e-9;
const joinNotes = (notes: string[]) => [...new Set(notes.map((note) => note.trim()).filter(Boolean))].join(' ');

/**
 * A cut added at `fraction`, the slice it parts keeping its note on both sides, or the cut nearest it within
 * `tolerance` removed, the notes of the two slices it parted joined. None within `tolerance` of the frame's edges: a
 * sliver slice would still be billed as a whole generation.
 */
export function toggleStreetCut(cuts: StreetCuts, fraction: number, tolerance: number): StreetCuts {
  const near = cuts.at.reduce((best, c, i) => (Math.abs(c - fraction) <= tolerance
    && (best < 0 || Math.abs(c - fraction) < Math.abs(cuts.at[best]! - fraction)) ? i : best), -1);
  if (near >= 0) {
    return { ...cuts, at: cuts.at.filter((_, i) => i !== near), notes: [...cuts.notes.slice(0, near), joinNotes(cuts.notes.slice(near, near + 2)), ...cuts.notes.slice(near + 2)] };
  }
  const margin = Math.max(tolerance, EPSILON);
  if (!(fraction > margin && fraction < 1 - margin)) return cuts;
  const slice = cuts.at.filter((c) => c < fraction).length;
  return { ...cuts, at: [...cuts.at.slice(0, slice), fraction, ...cuts.at.slice(slice)], notes: [...cuts.notes.slice(0, slice + 1), ...cuts.notes.slice(slice)] };
}

/**
 * Cuts turned with their panorama when its seam moves to `turn` (a fraction of the width): each cut moves by the same
 * turn modulo 1. The slice the new seam falls in becomes the first and the last, each keeping its note; the first and
 * last slices of before meet again as one, their notes joined.
 */
export function turnStreetCuts(cuts: StreetCuts, turn: number): StreetCuts {
  const wrap = (x: number) => ((x % 1) + 1) % 1;
  const at = cuts.at.map((c) => wrap(c - turn)).filter((c) => c > EPSILON && c < 1 - EPSILON).sort((a, b) => a - b);
  const before = [0, ...cuts.at, 1];
  // Each slice of before, where it lies after the turn: one span, or two when it now wraps over the seam.
  const spans = cuts.notes.flatMap((note, i) => {
    const from = wrap(before[i]! - turn), to = from + before[i + 1]! - before[i]!;
    return to <= 1 ? [{ from, to, note }] : [{ from, to: 1, note }, { from: 0, to: to - 1, note }];
  });
  const after = [0, ...at, 1];
  const notes = after.slice(1).map((to, j) => {
    const from = after[j]!;
    return joinNotes(spans
      .filter((span) => Math.min(to, span.to) - Math.max(from, span.from) > EPSILON)
      .sort((a, b) => Math.max(from, a.from) - Math.max(from, b.from))
      .map((span) => span.note));
  });
  return { ...cuts, at, notes };
}

/** A zone's world rect on a workspace frame: the frame is the whole equirectangular panorama. */
export const streetZoneRect = ({ lat, lon }: StreetZone, frame: Rect): Rect => ({
  x: frame.x + ((lon[0] + 180) / 360) * frame.w,
  y: frame.y + ((90 - lat[1]) / 180) * frame.h,
  w: ((lon[1] - lon[0]) / 360) * frame.w,
  h: ((lat[1] - lat[0]) / 180) * frame.h,
});

/** A Street View workspace's frame: one 2:1 equirectangular panorama. */
export const isStreetPanoramaFrame = (frame: Rect) => frame.h > 0 && Math.abs(frame.w / frame.h - 2) < 0.01;

/**
 * "Repair cuts", after a Develop 360 run: one square per interior cut, the main image sent with a red stroke on the cut,
 * of which only a strip around the stroke is kept. The panorama's own seam is not one: Set the seam moves it.
 */
export const STREET_REPAIR_RECIPE = {
  label: 'Repair cuts · Muse Image',
  model: 'muse-image',
  params: {},
  autoColorMatch: true,
  prompt: 'The red stroke marks a seam in this photograph of a 360° panorama. Replace it with what belongs there, so the scene continues across it. Change nothing else.',
  /** The stroke's width, a share of the square's side. */
  stroke: 0.02,
  /** The strip kept of each output, centred on the stroke, in stroke widths. */
  keptStrokes: 3,
  /** The kept strip's feathered edge, in the layer's pixels (a native layer cut's blend radius). */
  blendRadius: 16,
} as const;

type StreetRepairStep = {
  /** The square generated, the horizon band's captured latitudes high, centred on the cut. */
  zone: StreetZone;
  /** The red stroke, over the square's full height. */
  stroke: StreetZone;
  /** The share of the output's width kept, from and to its left edge. */
  kept: [number, number];
};

export function streetRepairSteps({ at }: StreetCuts): StreetRepairStep[] {
  const lat = bandCaptured(HORIZON.lat);
  // On the 2:1 equirectangular frame a degree is as long across as down: the square is as many degrees wide as high.
  const side = lat[1] - lat[0];
  const stroke = side * STREET_REPAIR_RECIPE.stroke;
  const kept = STREET_REPAIR_RECIPE.stroke * STREET_REPAIR_RECIPE.keptStrokes;
  return at.map((cut) => {
    const lon = cut * 360 - 180;
    return {
      zone: { lat, lon: [lon - side / 2, lon + side / 2] },
      stroke: { lat, lon: [lon - stroke / 2, lon + stroke / 2] },
      kept: [0.5 - kept / 2, 0.5 + kept / 2],
    };
  });
}

/** What keeps Repair cuts from running on these cuts of `frame`, refused before anything is billed; empty when it can. */
export function streetRepairErrors(cuts: StreetCuts | null, frame: Rect): string[] {
  if (enforceStudioModelMinimumGenerations(STREET_REPAIR_RECIPE.model, 1) !== 1) return [`The model ${STREET_REPAIR_RECIPE.model} cannot run one generation alone.`];
  if (!isStreetPanoramaFrame(frame)) return ['This workspace is not a 360° panorama.'];
  if (!cuts || !isStreetCuts(cuts) || cuts.at.length === 0) return ['There is no cut to repair: cut the panorama with Develop 360 first.'];
  const steps = streetRepairSteps(cuts);
  return steps.flatMap(({ zone }, i) => (zone.lon[0] < -180 - EPSILON || zone.lon[1] > 180 + EPSILON
    ? [`Cut ${i + 1} of ${steps.length} lies too close to the panorama's edge: its square would leave the frame.`] : []));
}

/** What keeps a recipe from running on these cuts of `frame`; empty when it can. */
export function streetDevelopErrors(recipe: StreetDevelopRecipe, cuts: StreetCuts, frame: Rect): string[] {
  const errors: string[] = [];
  // A run's price is one quote per slice: one generation per slice.
  if (enforceStudioModelMinimumGenerations(recipe.model, 1) !== 1) errors.push(`The model ${recipe.model} cannot run one generation alone.`);
  if (!isStreetCuts(cuts)) {
    errors.push('The cuts must lie inside the panorama, sorted, with one note per slice.');
    return errors;
  }
  // Refused before any launch, or a slice the model refuses would fail after the others were billed.
  streetDevelopSteps(recipe, cuts).forEach(({ zone }, i) => {
    try {
      recipe.checkZone(streetZoneRect(zone, frame));
    } catch (error) {
      errors.push(`Slice ${i + 1} cannot be generated, move or add a cut: ${error instanceof Error ? error.message : String(error)}`);
    }
  });
  return errors;
}
